import React, { useState, useEffect } from "react";
import { Select, InputNumber, Radio, Collapse, Button, Checkbox, Input, Tag } from "antd";
import { MinusCircleOutlined, SearchOutlined } from "@ant-design/icons";

const APPS_MODES = [
  { value: "all", label: "All apps" },
  { value: "only_selected", label: "Only selected apps" },
  { value: "except_selected", label: "All apps except selected" },
];

const MAX_BATCHES = 5;

const RoleCountrySettingsSection = ({
  countries = [],
  applications = [],
  value = {},
  onChange,
  errors = {},
}) => {
  const countrySettings = value?.country_settings ?? [];
  const batches = value?.batches ?? [];
  const [addCountryValue, setAddCountryValue] = useState(undefined);
  const [appSearchByCountry, setAppSearchByCountry] = useState({});

  const emitChange = (next) => {
    onChange?.({ ...value, ...next });
  };

  const addCountry = (countryId) => {
    const country = countries.find((c) => c.id === countryId);
    if (!country) return;
    if (countrySettings.some((cs) => cs.country_id === countryId)) return;
    const newCs = {
      country_id: countryId,
      country_name: country.name,
      apps_mode: "all",
      application_ids: [],
    };
    const nextCountrySettings = [...countrySettings, newCs];
    const nextBatches = batches.length > 0 ? batches : [{ batch_number: 1, otps_to_unlock_next: 0, country_sims: {} }];
    nextBatches.forEach((b, i) => {
      const countrySims = { ...(b.country_sims || {}) };
      countrySims[countryId] = i === 0 ? 0 : (b.country_sims?.[countryId] ?? 0);
      nextBatches[i] = { ...b, country_sims: countrySims };
    });
    if (batches.length === 0) {
      nextBatches[0].country_sims[countryId] = 0;
    }
    emitChange({ country_settings: nextCountrySettings, batches: nextBatches });
    setAddCountryValue(undefined);
  };

  const removeCountry = (countryId) => {
    const nextCountrySettings = countrySettings.filter((cs) => cs.country_id !== countryId);
    const nextBatches = batches.map((b) => {
      const countrySims = { ...(b.country_sims || {}) };
      delete countrySims[countryId];
      return { ...b, country_sims: countrySims };
    });
    emitChange({ country_settings: nextCountrySettings, batches: nextBatches });
  };

  const updateCountrySetting = (countryId, updates) => {
    emitChange({
      country_settings: countrySettings.map((cs) =>
        cs.country_id === countryId ? { ...cs, ...updates } : cs
      ),
    });
  };

  const setBatchCount = (count) => {
    const num = Math.max(1, Math.min(MAX_BATCHES, parseInt(count, 10) || 1));
    const nextBatches = [...Array(num)].map((_, i) => {
      const prev = batches[i];
      const countrySims = {};
      countrySettings.forEach((cs) => {
        countrySims[cs.country_id] = prev?.country_sims?.[cs.country_id] ?? 0;
      });
      return {
        batch_number: i + 1,
        otps_to_unlock_next: i === 0 ? 0 : (prev?.otps_to_unlock_next ?? 0),
        country_sims: countrySims,
      };
    });
    emitChange({ batches: nextBatches });
  };

  const updateBatchSims = (batchIndex, countryId, val) => {
    const b = batches[batchIndex];
    if (!b) return;
    const countrySims = { ...(b.country_sims || {}), [countryId]: val ?? 0 };
    const nextBatches = [...batches];
    nextBatches[batchIndex] = { ...b, country_sims: countrySims };
    emitChange({ batches: nextBatches });
  };

  const updateBatchOtps = (batchIndex, val) => {
    const nextBatches = [...batches];
    nextBatches[batchIndex] = { ...nextBatches[batchIndex], otps_to_unlock_next: val ?? 0 };
    if (batchIndex === 0) nextBatches[0].otps_to_unlock_next = 0;
    emitChange({ batches: nextBatches });
  };

  const toggleApp = (countryId, appId) => {
    const cs = countrySettings.find((c) => c.country_id === countryId);
    if (!cs) return;
    const ids = cs.application_ids || [];
    const next = ids.includes(appId)
      ? ids.filter((id) => id !== appId)
      : [...ids, appId];
    updateCountrySetting(countryId, { application_ids: next });
  };


  const availableCountries = countries.filter(
    (c) => !countrySettings.some((cs) => cs.country_id === c.id)
  );

  const numBatches = batches.length || 1;

  return (
    <div className="role-country-settings-section">
      <h4 style={{ marginBottom: 12, fontWeight: 600 }}>1. Allowed Countries</h4>
      {availableCountries.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Select
            placeholder="Add a country"
            style={{ width: "100%", maxWidth: 280 }}
            options={availableCountries.map((c) => ({
              value: c.id,
              label: `${c.name} (+${c.dialing_code || ""})`,
            }))}
            onSelect={addCountry}
            value={addCountryValue}
            onChange={(v) => setAddCountryValue(v)}
            allowClear
            showSearch
            optionFilterProp="label"
            getPopupContainer={(node) => node?.parentElement ?? document.body}
          />
        </div>
      )}

      {countrySettings.length === 0 && (
        <p style={{ color: "#64748b", fontSize: 13 }}>
          No countries added. Select at least one country to configure batches and apps.
        </p>
      )}

      {countrySettings.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#64748b", marginRight: 4 }}>Selected:</span>
          {countrySettings.map((cs) => {
            const country = countries.find((c) => c.id === cs.country_id);
            const label = country?.name || cs.country_name || `Country ${cs.country_id}`;
            return (
              <Tag
                key={cs.country_id}
                closable
                onClose={() => removeCountry(cs.country_id)}
                style={{ marginBottom: 0 }}
              >
                {label}
              </Tag>
            );
          })}
        </div>
      )}

      {countrySettings.length > 0 && (
        <>
          <h4 style={{ marginTop: 24, marginBottom: 12, fontWeight: 600 }}>2. Batches</h4>
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>Number of batches (1–5):</span>
            <InputNumber
              min={1}
              max={MAX_BATCHES}
              value={numBatches}
              onChange={(v) => setBatchCount(v)}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            {batches.map((b, batchIdx) => (
              <div
                key={b.batch_number}
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f8fafc",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Batch {batchIdx + 1}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 8 }}>
                  {countrySettings.map((cs) => (
                    <div key={cs.country_id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ minWidth: 60 }}>{cs.country_name}:</span>
                      <InputNumber
                        min={0}
                        placeholder="Sims"
                        value={b.country_sims?.[cs.country_id] ?? 0}
                        onChange={(v) => updateBatchSims(batchIdx, cs.country_id, v)}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ minWidth: 120 }}>OTPs to unlock next:</span>
                  <InputNumber
                    min={0}
                    disabled={batchIdx === 0}
                    value={b.otps_to_unlock_next ?? 0}
                    onChange={(v) => updateBatchOtps(batchIdx, v)}
                  />
                  {batchIdx === 0 && (
                    <span style={{ color: "#64748b", fontSize: 12 }}>(0 for first batch)</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h4 style={{ marginBottom: 12, fontWeight: 600 }}>3. Allowed Apps (per country)</h4>
          <Collapse
            ghost
            items={countrySettings.map((cs) => {
              const country = countries.find((c) => c.id === cs.country_id);
              return {
                key: cs.country_id,
                label: (
                  <span style={{ fontWeight: 500 }}>
                    {country?.name || cs.country_name || `Country ${cs.country_id}`}
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<MinusCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCountry(cs.country_id);
                      }}
                      style={{ marginLeft: 8 }}
                    >
                      Remove
                    </Button>
                  </span>
                ),
                children: (
                  <div style={{ paddingLeft: 8, paddingBottom: 16 }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ marginBottom: 8, fontWeight: 500 }}>Allowed apps</div>
                      <Radio.Group
                        value={cs.apps_mode || "all"}
                        onChange={(e) =>
                          updateCountrySetting(cs.country_id, {
                            apps_mode: e.target.value,
                            application_ids: e.target.value === "all" ? [] : cs.application_ids || [],
                          })
                        }
                      >
                        {APPS_MODES.map((m) => (
                          <Radio key={m.value} value={m.value}>
                            {m.label}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </div>
                    {(cs.apps_mode === "only_selected" || cs.apps_mode === "except_selected") && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ marginBottom: 8, fontSize: 13 }}>
                          {cs.apps_mode === "only_selected"
                            ? "Select allowed apps:"
                            : "Select apps to exclude:"}
                        </div>
                        <Input
                          placeholder="Search apps..."
                          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                          value={appSearchByCountry[cs.country_id] ?? ""}
                          onChange={(e) =>
                            setAppSearchByCountry((prev) => ({
                              ...prev,
                              [cs.country_id]: e.target.value,
                            }))
                          }
                          allowClear
                          style={{ marginBottom: 8, borderRadius: 8 }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            maxHeight: 200,
                            overflowY: "auto",
                            padding: 8,
                            background: "#f8fafc",
                            borderRadius: 8,
                          }}
                        >
                          {applications
                            .filter((app) => {
                              const q = (appSearchByCountry[cs.country_id] ?? "").trim().toLowerCase();
                              return !q || (app.name || "").toLowerCase().includes(q);
                            })
                            .map((app) => (
                              <Checkbox
                                key={app.id}
                                checked={(cs.application_ids || []).includes(app.id)}
                                onChange={() => toggleApp(cs.country_id, app.id)}
                              >
                                {app.name}
                              </Checkbox>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />
        </>
      )}
      {errors.country_settings && (
        <p className="text-red-500 text-xs" style={{ marginTop: 8 }}>
          {errors.country_settings}
        </p>
      )}
    </div>
  );
};

export default RoleCountrySettingsSection;
